import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { SxProps, Theme } from "@mui/material/styles";
import { useContext } from "react";
import { APIContext } from "../../Contexts/APIContext";
import { SessionContext } from "../../Contexts/SessionContext";
import ResponsiveTable from "../../Utilities/ResponsiveTable";
import SortableTableHead, { HeadCell } from "../../Utilities/SortableTableHead";
import RankOrderRowViewModel from "../ViewModels/RankOrderRowViewModel";
import RankOrderViewModel from "../ViewModels/RankOrderViewModel";
import RankOrderRow from "./RankOrderRow";

const truncatedColumn = (width: string): SxProps<Theme> => ({
    width,
    minWidth: width,
    maxWidth: width,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const headerColumn = (width: string): SxProps<Theme> => ({
    width,
    minWidth: width,
    whiteSpace: 'nowrap',
});

const rankOrderColumnSx = {
    rank: truncatedColumn('calc(4ch + 16px)'),
    reference: truncatedColumn('calc(11.5ch + 16px)'),
    description: truncatedColumn('calc(25ch + 16px)'),
    town: truncatedColumn('calc(18ch + 16px)'),
    gfa: truncatedColumn('calc(7ch + 16px)'),
    dayOfWeek: truncatedColumn('calc(10.5ch + 16px)'),
    date: truncatedColumn('calc(10ch + 16px)'),
    movement: truncatedColumn('calc(10ch + 16px)'),
};

const rankOrderHeaderColumnSx = {
    rank: headerColumn('calc(4ch + 16px)'),
    reference: headerColumn('calc(11.5ch + 16px)'),
    description: headerColumn('calc(25ch + 16px)'),
    town: headerColumn('calc(18ch + 16px)'),
    gfa: headerColumn('calc(7ch + 16px)'),
    dayOfWeek: headerColumn('calc(15ch + 16px)'),
    date: headerColumn('calc(10ch + 16px)'),
    movement: headerColumn('calc(10ch + 16px)'),
};

interface IProps {
    rankOrder: RankOrderViewModel;
    count: number;
    clickToggle: string;
    searchValue: string;
}

export default function RankOrderTable({ rankOrder, count = 0, clickToggle, searchValue }: IProps) {
    const { updateSessionState, sessionState } = useContext(SessionContext);
    const { loading } = useContext(APIContext);

    const headCells: HeadCell<RankOrderRowViewModel>[] = [
        { id: 'rank', property: 'Rank', align: "center", disablePadding: true, label: 'Rank', show: true, disableSort: true, sx: rankOrderHeaderColumnSx.rank },
        { id: 'siteRef', property: 'SiteRef', align: "center", disablePadding: true, label: 'Reference', show: true, disableSort: true, sx: rankOrderHeaderColumnSx.reference },
        { id: 'description', property: 'Description', align: "center", disablePadding: true, label: 'Description', show: true, disableSort: true, sx: rankOrderHeaderColumnSx.description },
        { id: 'location', property: 'Location', align: "center", disablePadding: true, label: 'Town/City', show: true, disableSort: true, sx: rankOrderHeaderColumnSx.town },
        { id: 'parameter', property: 'Parameter', align: "center", disablePadding: true, label: rankOrder.abbreviation ?? "", show: true, disableSort: true, sx: rankOrder.abbreviation?.trim().toUpperCase() === 'GFA' ? rankOrderHeaderColumnSx.gfa : undefined },
        { id: 'day', property: 'Day', align: "center", disablePadding: true, label: 'Day of the Week', show: true, disableSort: true, sx: rankOrderHeaderColumnSx.dayOfWeek },
        { id: 'date', property: 'Date', align: "center", disablePadding: true, label: 'Date', show: true, disableSort: true, sx: rankOrderHeaderColumnSx.date },
        { id: 'peakHour', property: 'PeakHour', align: "center", disablePadding: true, label: 'Peak Hour', show: sessionState.rankOrderPeakHour, disableSort: true },
        { id: 'arrivals', property: 'Arrivals', align: "center", disablePadding: true, label: 'Arrivals', show: true, sx: rankOrderHeaderColumnSx.movement },
        { id: 'departures', property: 'Departures', align: "center", disablePadding: true, label: 'Departures', show: true, sx: rankOrderHeaderColumnSx.movement },
        { id: 'totals', property: 'Totals', align: "center", disablePadding: true, label: 'Totals', show: true, sx: rankOrderHeaderColumnSx.movement },
        { id: 'parkingSpacesPerDwelling', property: 'ParkingSpacesPerDwelling', align: 'center', disablePadding: true, label: 'Parking Spaces Per Dwelling', show: !!(rankOrder?.rows ?? []).some(r => r.parkingSpacesPerDwelling != null), disableSort: true }
    ];

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof RankOrderRowViewModel) => {
        const isAsc = sessionState.rankOrderSort === property && sessionState.rankOrderDir === 'asc';
        updateSessionState({
            rankOrderSort: property,
            rankOrderDir: isAsc ? 'desc' : 'asc',
            rankOrderPage: 0
        });
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        updateSessionState({
            rankOrderPage: newPage
        });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateSessionState({
            rankOrderPage: 0,
            rankOrderRows: parseInt(event.target.value)
        });
    };

    return (
        <Grid container component={Paper}>
            <TableContainer>
                <ResponsiveTable
                    size={"small"}
                    sx={{
                        width: '100%',
                        tableLayout: 'auto',
                        '& .MuiTableCell-root': {
                            fontSize: '0.8125rem',
                            px: 1,
                        },
                        '& .MuiTableCell-head': { fontWeight: 700 },
                        '& .MuiTableSortLabel-root': { fontWeight: 700 },
                    }}
                >
                    <SortableTableHead
                        order={sessionState.rankOrderDir}
                        orderBy={sessionState.rankOrderSort}
                        onRequestSort={handleRequestSort}
                        headCells={headCells}
                    />
                    <TableBody>
                        {!loading &&
                            rankOrder.rows?.map((row: RankOrderRowViewModel) => (
                                <RankOrderRow
                                    key={`${row.rank}-${row.siteRef}-${row.date}`}
                                    row={row}
                                    rank={row.rank ?? 0}
                                    percentile15Rank={rankOrder.percentile15Rank}
                                    percentile85Rank={rankOrder.percentile85Rank}
                                    type={'rankorder'}
                                    clickToggle={clickToggle}
                                    searchValue={searchValue}
                                    isGfa={rankOrder.abbreviation?.trim().toUpperCase() === 'GFA'}
                                    columnSx={rankOrderColumnSx}
                                />
                            ))
                        }
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={12} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </ResponsiveTable>
            </TableContainer>
            <Box>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    component="div"
                    count={count}
                    rowsPerPage={sessionState.rankOrderRows}
                    page={sessionState.rankOrderPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </Grid>
    );
}
